// utils/mapFeedItems.js
import { extractColorValue } from "./extractColorValue";

// Map Pin Level Permissions (bitmask)
const pinLevelMap = {
  1: "Guest", //Not used
  2: "AssociateIbo",
  4: "Platinum",
  8: "Emerald",
  16: "Diamond",
};

const dropdownReunionOptions = [
  {
    name: "Puryear Family Reunion",
    id: 8192,
  },
  {
    name: "Duncan Family Reunion",
    id: 15,
  },
  {
    name: "Rice Family Reunion",
    id: 1048576,
  },
  {
    name: "Baker Family Reunion",
    id: 16,
  },
  {
    name: "Whalen Family Reunion",
    id: 17,
  },
];

export const mapPinLevelPermissions = (value) => {
  return Object.keys(pinLevelMap)
    .filter((key) => value & key)
    .map((key) => pinLevelMap[key]);
};

function normalizeReunionOptions(input) {
  if (!input) return [];

  let ids = Array.isArray(input)
    ? input
    : input.split(",").map((x) => x.trim());

  ids = ids.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));

  return ids
    .map((id) => dropdownReunionOptions.find((opt) => opt.id === id)?.name)
    .filter(Boolean);
}

function normalizeDiamondOptions(input, diamondsList = []) {
  if (!input) return [];

  let ibos = Array.isArray(input)
    ? input
    : input.split(",").map((x) => x.trim());

  ibos = ibos.map((ibo) => parseInt(ibo, 10)).filter((ibo) => !isNaN(ibo));

  return ibos
    .map((ibo) => diamondsList.find((opt) => opt.IBO == ibo)?.name)
    .filter(Boolean);
}

function normalizeGroupOptions(input, groupsList = []) {
  if (!input) return [];

  let groupId = Array.isArray(input)
    ? input
    : input.split(",").map((x) => x.trim());

  groupId = groupId
    .map((groupId) => parseInt(groupId, 10))
    .filter((groupId) => !isNaN(groupId));

  return groupId
    .map((groupId) => groupsList.find((opt) => opt.groupId == groupId)?.name)
    .filter(Boolean);
}

// Map Platforms (bitmask)
const platformMap = {
  1: "Web",
  2: "iOS",
  4: "Android",
};

const DEFAULT_CARD_IMAGE_URL =
  "https://wwgus.blob.core.windows.net/icons/bsm-image-coming-soon.png";
const LEGACY_DEFAULT_CARD_IMAGE_URLS = [
  "https://wwgusdev.blob.core.windows.net/home-feed/bsm-image-coming-soon.png",
];

function normalizeCardImageUrl(url) {
  const normalized = (url || "").trim();
  if (!normalized) {
    return DEFAULT_CARD_IMAGE_URL;
  }

  const lower = normalized.toLowerCase();
  if (
    LEGACY_DEFAULT_CARD_IMAGE_URLS.some(
      (legacyUrl) => lower === legacyUrl.toLowerCase()
    )
  ) {
    return DEFAULT_CARD_IMAGE_URL;
  }

  return normalized;
}

export const mapPlatforms = (value) => {
  return Object.keys(platformMap)
    .filter((key) => value & key)
    .map((key) => platformMap[key]);
};

// Map full feed item
function safeParseData(data) {
  // Handles:
  // - an object already
  // - a single-stringified JSON: '{"card":...}'
  // - a double-stringified JSON: '"{\"card\":...}"'
  if (!data) return {};
  let parsed = data;
  try {
    // If it's a string, try parsing
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }
  } catch (e) {
    // If initial parse failed, try to unquote an extra-wrapped string
    if (
      typeof data === "string" &&
      data.length > 2 &&
      data.startsWith('"') &&
      data.endsWith('"')
    ) {
      // remove the outer quotes and unescape inner quotes
      const unwrapped = data.slice(1, -1).replace(/\\"/g, '"');
      try {
        parsed = JSON.parse(unwrapped);
      } catch (e2) {
        // give up and return empty object
        return {};
      }
    } else {
      return {};
    }
  }

  // If parsing returned a string (parsed twice), attempt one more parse
  let attempts = 0;
  while (typeof parsed === "string" && attempts < 3) {
    try {
      parsed = JSON.parse(parsed);
    } catch (err) {
      break;
    }
    attempts += 1;
  }

  return parsed || {};
}

export const mapApiResponseToFeedItem = (item, diamondsList, groupsList) => {
  const rawData = safeParseData(item.data);
  // Draft payload can arrive either nested in data or as a top-level field.
  // Parse both shapes so draft edits map consistently in feed + edit pages.
  const rawDraftVersion = rawData?.draftVersion ?? item?.draftVersion ?? null;
  const parsedDraftVersion = rawDraftVersion ? safeParseData(rawDraftVersion) : null;
  const draftVersion =
    parsedDraftVersion && typeof parsedDraftVersion === "object"
      ? parsedDraftVersion
      : rawDraftVersion;
  const effectiveItem =
    item.isEditingDraft && draftVersion && typeof draftVersion === "object"
      ? { ...item, ...draftVersion }
      : item;
  const effectiveRawData =
    item.isEditingDraft &&
    draftVersion &&
    typeof draftVersion === "object" &&
    draftVersion.data != null
      ? safeParseData(draftVersion.data)
      : rawData;
  const draftOptions =
    draftVersion && typeof draftVersion === "object" ? draftVersion.options : null;

  if (item.id === 1739) {
    // console.log(rawData, item);
  }
  // Patch missing shape defensively}
  const parsedData = {
    backgroundType: "On Gradient",
    bottomPadding: true,
    gradientAngle: effectiveRawData?.card?.gradient?.angle || "45deg",
    gradientColors: effectiveRawData?.card?.gradient?.stops
      ? effectiveRawData.card.gradient.stops.map((s) => (typeof s === "string" ? s : s.color))
      : ["color-a", "color-b"],
    // header: "* Card Title *",
    headerBackground: { backgroundColor: "default", backgroundOpacity: "0.80" },
    headerTextColor: "default",
    horizontalAlign: "center",
    imgSrc: "",
    maxHeight: "",
    subHeader: "",
    subHeaderTextColor: "default",

    ...effectiveRawData,

    card: {
      ...effectiveRawData?.card,
      details: {
        ...effectiveRawData?.card?.details,
        creator: {
          text: "",
          color: "#FFFFFF",
          ...effectiveRawData?.card?.details?.creator,
        },
        sku: { text: "", color: "#FFFFFF", ...effectiveRawData?.card?.details?.sku },
        title: {
          text: "",
          color: "#000000",
          ...effectiveRawData?.card?.details?.title,
        },
      },
      gradient: (() => {
        const g = effectiveRawData?.card?.gradient || {};
        const rawAngle = g.angle;
        const angle =
          typeof rawAngle === "string"
            ? Number(rawAngle.replace(/deg\s*$/i, "")) || 0
            : rawAngle ?? 0;
        const rawStops = Array.isArray(g.stops) ? g.stops : [];
        const stops =
          rawStops.length > 0
            ? rawStops.map((s) => (typeof s === "string" ? { color: s } : s))
            : [{ color: "#FFFFFF" }, { color: "#FFFFFF" }];
        return { ...g, angle, stops };
      })(),
      header: {
        topTitle: {
          text: "",
          color: "#FFFFFF",
          ...effectiveRawData?.card?.header?.topTitle,
        },
        title: {
          text: "",
          color: "#000000",
          ...effectiveRawData?.card?.header?.title,
        },
        subTitle: {
          text: "",
          color: "#FFFFFF",
          ...effectiveRawData?.card?.header?.subTitle,
        },
        ...effectiveRawData?.card?.header,
      },
      image: {
        alignment: "center",
        path: "",
        verticalAlignment: "middle",
        subText: { text: "", color: "#000000" },
        ...effectiveRawData?.card?.image,
      },
    },
    content: {
      sections: (effectiveRawData?.content?.sections || []).map((section) => {
        const mappedSection = {
          ...section,
          // Ensure backgroundColor is preserved in rgba format
          backgroundColor: section.backgroundColor || "rgba(255,255,255,1.00)",
        };
        return mappedSection;
      }),
    },
    draftVersion,
  };

  if (effectiveRawData?.image && !parsedData?.card?.image?.path) {
    parsedData.card = parsedData.card || {};
    parsedData.card.image = parsedData.card.image || {};
    parsedData.card.image.path = effectiveRawData.image;
    // keep top-level image property for backwards compat
    parsedData.image = parsedData.image || effectiveRawData.image;
    parsedData.img = parsedData.img || effectiveRawData.image;
  }

  if (
    (effectiveRawData?.position || effectiveRawData?.align) &&
    !parsedData.horizontalAlign
  ) {
    parsedData.horizontalAlign =
      effectiveRawData.position || effectiveRawData.align;
    parsedData.position =
      parsedData.position || effectiveRawData.position || effectiveRawData.align;
    parsedData.align =
      parsedData.align || effectiveRawData.align || effectiveRawData.position;
  }

  const templateTypeMap = {
    "standing-order": "Standing Order",
    standard: "Standard Content",
    banner: "Banner",
  };

  const normalizedTemplate = (effectiveItem.template || item.template)?.toLowerCase();

  if (
    normalizedTemplate === "standard" ||
    normalizedTemplate === "standing-order"
  ) {
    const normalizedImagePath = normalizeCardImageUrl(
      parsedData?.card?.image?.path ?? parsedData?.imgSrc ?? parsedData?.image
    );
    parsedData.card = parsedData.card || {};
    parsedData.card.image = parsedData.card.image || {};
    parsedData.card.image.path = normalizedImagePath;
    parsedData.imgSrc = normalizeCardImageUrl(parsedData?.imgSrc || normalizedImagePath);
  }

  // Debugging -- Ignore
  /*
  if (!templateTypeMap[normalizedTemplate]) {
    console.warn(
      "Unexpected Template:",
      item.Template,
      "for Item ID:",
      item.Id
    );
  }
  */

  const baseItem = {
    type: templateTypeMap[normalizedTemplate] || "Unknown",
    options: {
      availableOn:
        effectiveItem.availableOn ??
        effectiveItem.AvailableOn ??
        draftOptions?.availableOn ??
        item.availableOn,
      group: effectiveItem.group ?? draftOptions?.group ?? item.group,
      id: effectiveItem.id ?? effectiveItem.Id ?? item.id ?? item.Id,
      isEditingDraft: item.isEditingDraft,
      managementApproved:
        effectiveItem.managementApproved ??
        draftOptions?.managementApproved ??
        item.managementApproved,
      familyReunions: normalizeReunionOptions(
        effectiveItem.familyReunions ?? item.familyReunions
      ),
      diamonds: normalizeDiamondOptions(
        effectiveItem.diamonds ?? item.diamonds,
        diamondsList
      ),
      groups: normalizeGroupOptions(
        effectiveItem.groups ?? item.groups,
        groupsList
      ),
      pinLevelPermissions: mapPinLevelPermissions(
        effectiveItem.pinLevelPermissions ?? item.pinLevelPermissions
      ),
      platforms: mapPlatforms(effectiveItem.platforms ?? item.platforms),
      previewIBO:
        effectiveItem.previewIBO ??
        effectiveItem.previewIbo ??
        item.previewIBO ??
        item.previewIbo ??
        0,
      priority: effectiveItem.priority ?? draftOptions?.priority ?? item.priority,
      status: effectiveItem.status ?? draftOptions?.status ?? item.status,
      syncFeedId: effectiveItem.syncFeedId ?? item.syncFeedId,
      syncTo: effectiveItem.syncTo ?? item.syncTo,
      template: effectiveItem.template ?? item.template,
      title:
        effectiveItem.title ??
        effectiveItem.Title ??
        draftOptions?.title ??
        effectiveRawData?.card?.header?.title?.text ??
        item.title,
      unpublishOn: effectiveItem.unpublishOn ?? item.unpublishOn,
      unpublishType: effectiveItem.unpublishType ?? item.unpublishType,
      draftVersion,
    },
  };

  // Normalize image path
  let imgSrc = "";
  if (normalizedTemplate === "banner") {
    // banner may use image or shorthand img
    imgSrc =
      parsedData.image ?? parsedData.img ?? parsedData.card?.image?.path ?? "";
    // If shorthand align exists, set horizontalAlign for UI consistency
    if (!parsedData.horizontalAlign && parsedData.align)
      parsedData.horizontalAlign = parsedData.align;
  } else if (
    (normalizedTemplate === "standard" ||
      normalizedTemplate === "standing-order") &&
    parsedData?.card?.image?.path
  ) {
    imgSrc = parsedData.card.image.path;
  }

  // Normalize date
  let date = "";
  if (
    normalizedTemplate === "standing-order" ||
    normalizedTemplate === "standard"
  ) {
    date = effectiveItem.AvailableOn ?? effectiveItem.availableOn ?? item.AvailableOn;
  } else if (normalizedTemplate === "banner" && parsedData?.date) {
    date = parsedData.date;
  }

  if (normalizedTemplate === "standing-order") {
    return { ...baseItem, data: parsedData, imgSrc, date };
  } else if (
    normalizedTemplate === "standard" ||
    normalizedTemplate === "banner"
  ) {
    // Normalize any label-prefixed color strings stored by SelectColorInput
    // so all consumers (FeedItem, modal preview, IBO app) get plain CSS values.
    const normalizedParsedData = {
      ...parsedData,
      headerTextColor: extractColorValue(parsedData.headerTextColor, "#FFFFFF"),
      subHeaderTextColor: extractColorValue(parsedData.subHeaderTextColor, "#FFFFFF"),
      gradientColors: Array.isArray(parsedData.gradientColors)
        ? parsedData.gradientColors.map((c) => extractColorValue(c, "#FFFFFF"))
        : parsedData.gradientColors,
      card: parsedData.card
        ? {
            ...parsedData.card,
            header: parsedData.card.header
              ? {
                  ...parsedData.card.header,
                  topTitle: parsedData.card.header.topTitle
                    ? {
                        ...parsedData.card.header.topTitle,
                        color: extractColorValue(parsedData.card.header.topTitle.color, "#FFFFFF"),
                      }
                    : parsedData.card.header.topTitle,
                  title: parsedData.card.header.title
                    ? {
                        ...parsedData.card.header.title,
                        color: extractColorValue(parsedData.card.header.title.color, "#000000"),
                      }
                    : parsedData.card.header.title,
                  subTitle: parsedData.card.header.subTitle
                    ? {
                        ...parsedData.card.header.subTitle,
                        color: extractColorValue(parsedData.card.header.subTitle.color, "#FFFFFF"),
                      }
                    : parsedData.card.header.subTitle,
                }
              : parsedData.card.header,
          }
        : parsedData.card,
    };
    return { ...baseItem, information: normalizedParsedData, imgSrc, date };
  }

  return { ...baseItem, imgSrc, date }; // Fallback (shouldn't hit this)
};
